import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const OrganizationStatsChart = ({ teacherStatsData }) => {
  const colors = [
    "#2B3674",
    "#D200A5",
    "#707EAE",
    "#FF8042",
    "#0088FE",
    "#00C49F",
    "#FFBB28",
  ];

  // Get all unique organization keys from the data
  const orgKeys = useMemo(() => {
    if (!teacherStatsData || teacherStatsData.length === 0) return [];
    return [
      ...new Set(
        teacherStatsData.flatMap((item) =>
          Object.keys(item).filter((key) => key !== "date")
        )
      ),
    ];
  }, [teacherStatsData]);

  // Process data to ensure all organizations have values for every date and make it cumulative
  const processedData = useMemo(() => {
    if (!teacherStatsData || teacherStatsData.length === 0) return [];

    // Ensure all keys have values (0 if missing)
    const dataWithAllKeys = teacherStatsData.map((item) => {
      const processedItem = { ...item };
      orgKeys.forEach((key) => {
        if (!(key in processedItem)) {
          processedItem[key] = 0;
        }
      });
      return processedItem;
    });

    // Make the data cumulative
    const cumulativeData = [];
    const runningTotals = {};

    orgKeys.forEach((key) => {
      runningTotals[key] = 0;
    });

    dataWithAllKeys.forEach((item) => {
      const cumulativeItem = { ...item };

      orgKeys.forEach((key) => {
        runningTotals[key] += Number(item[key]) || 0;
        cumulativeItem[key] = runningTotals[key];
      });

      cumulativeData.push(cumulativeItem);
    });

    return cumulativeData;
  }, [teacherStatsData, orgKeys]);

  const shouldScroll = processedData?.length > 10;

  return (
    <div
      style={{
        width: "100%",
        maxWidth: "100%",
        overflowX: shouldScroll ? "auto" : "visible",
        overflowY: "hidden",
        boxSizing: "border-box",
        scrollbarWidth: "auto",
        msOverflowStyle: "auto",
      }}
      className="chart-scroll-container"
    >
      <div
        style={{
          width: shouldScroll ? `${processedData.length * 180}px` : "100%",
          minWidth: "100%",
          boxSizing: "border-box",
        }}
      >
        <ResponsiveContainer height={300}>
          <LineChart
            data={processedData}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F4F7FE" />
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#A3AED0", fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={60}
              interval={0}
            />
            <YAxis axisLine={false} tickLine={false} tick={{ fill: "#A3AED0", fontSize: 12 }} />
            <Tooltip
              content={({ payload, label }) => {
                if (payload && payload.length > 0) {
                  const total = payload.reduce(
                    (sum, item) => sum + Number(item.value),
                    0
                  );

                  return (
                    <div
                      style={{
                        backgroundColor: "white",
                        padding: "10px",
                        border: "none",
                        borderRadius: "10px",
                        boxShadow: "0px 18px 40px rgba(112, 144, 176, 0.12)",
                      }}
                    >
                      <p style={{ fontWeight: "bold", color: "#2B3674", marginBottom: "5px" }}>{label}</p>
                      {payload.map((item, index) => (
                        <div key={index} style={{ color: item.color, fontSize: "14px", display: "flex", justifyContent: "space-between", gap: "20px" }}>
                          <span>{item.name}:</span>
                          <span style={{ fontWeight: "bold" }}>{item.value}</span>
                        </div>
                      ))}
                      <div
                        style={{
                          borderTop: "1px solid #F4F7FE",
                          marginTop: "8px",
                          paddingTop: "8px",
                          fontWeight: "bold",
                          color: "#2B3674",
                          display: "flex",
                          justifyContent: "space-between"
                        }}
                      >
                        <span>Total:</span>
                        <span>{total}</span>
                      </div>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend
              iconType="circle"
              wrapperStyle={{ paddingTop: "20px" }}
            />
            {orgKeys.map((key, index) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={colors[index % colors.length]}
                strokeWidth={3}
                dot={{ r: 4, fill: colors[index % colors.length] }}
                name={key}
                connectNulls={true}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default OrganizationStatsChart;
